import React, { useEffect, useRef, useState } from 'react';

import { Filter, Search, X } from 'lucide-react';

import { Button, Input } from './';

export interface FilterOption {
	id: string;
	label: string;
	value: string;
}

export interface FilterGroup {
	id: string;
	title: string;
	options: FilterOption[];
	selectedValues: string[];
	onSelectionChange: (values: string[]) => void;
	showSearch?: boolean;
	searchPlaceholder?: string;
}

export interface FilterDropdownProps {
	// Single filter mode (backward compatible)
	options?: FilterOption[];
	selectedValues?: string[];
	onSelectionChange?: (values: string[]) => void;
	searchPlaceholder?: string;
	title?: string;
	showSearch?: boolean;

	// Grouped filters mode
	groups?: FilterGroup[];

	// Common props
	placeholder?: string;
	className?: string;
	buttonClassName?: string;
	variant?: 'outline' | 'primary' | 'secondary';
	size?: 'sm' | 'md' | 'lg';
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
	// Single filter mode props
	options,
	selectedValues,
	onSelectionChange,
	searchPlaceholder = 'Search',
	title,
	showSearch = false,

	// Grouped mode props
	groups,

	// Common props
	placeholder = 'Filter',
	className = '',
	buttonClassName = '',
	variant = 'outline',
	size = 'md',
}) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
		{},
	);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Determine if we're in grouped mode
	const isGroupedMode = !!groups && groups.length > 0;

	// Click outside handler
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setShowDropdown(false);
				setSearchQueries({});
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Get total selected count across all groups
	const getTotalSelectedCount = (): number => {
		if (isGroupedMode) {
			return groups!.reduce(
				(total, group) => total + group.selectedValues.length,
				0,
			);
		}
		return selectedValues?.length || 0;
	};

	// Get button text
	const getButtonText = () => {
		const count = getTotalSelectedCount();
		if (count === 0) return placeholder;
		return `${placeholder} (${count})`;
	};

	// Clear all filters across all groups
	const clearAllFilters = () => {
		if (isGroupedMode) {
			groups!.forEach((group) => group.onSelectionChange([]));
		} else if (onSelectionChange) {
			onSelectionChange([]);
		}
		setSearchQueries({});
	};

	// Render a single filter group
	const renderFilterGroup = (
		group: FilterGroup,
		index: number,
		isLast: boolean,
	) => {
		const searchQuery = searchQueries[group.id] || '';
		const filteredOptions = group.options.filter(
			(option) =>
				option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
				option.value.toLowerCase().includes(searchQuery.toLowerCase()),
		);

		const toggleSelection = (value: string) => {
			const newSelection = group.selectedValues.includes(value)
				? group.selectedValues.filter((v) => v !== value)
				: [...group.selectedValues, value];
			group.onSelectionChange(newSelection);
		};

		return (
			<div key={group.id} className="self-stretch">
				{/* Group Title */}
				<div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
					{group.title}
					{group.selectedValues.length > 0 && (
						<span className="ml-1 text-purple-600 normal-case">
							({group.selectedValues.length})
						</span>
					)}
				</div>

				{/* Group Search */}
				{group.showSearch && (
					<div className="mb-2">
						<Input
							type="text"
							leftIcon={Search}
							value={searchQuery}
							onChange={(e) =>
								setSearchQueries((prev) => ({
									...prev,
									[group.id]: e.target.value,
								}))
							}
							placeholder={group.searchPlaceholder || 'Search...'}
							className="flex-1 border-none outline-none border-gray-200"
							variant="filled"
							rightIcon={searchQuery ? X : undefined}
							onRightIconClick={
								searchQuery
									? () =>
											setSearchQueries((prev) => ({
												...prev,
												[group.id]: '',
											}))
									: undefined
							}
						/>
					</div>
				)}

				{/* Group Options */}
				<div className="flex flex-col gap-1 max-h-32 overflow-y-auto">
					{filteredOptions.map((option) => (
						<label
							key={option.id}
							className="inline-flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-md p-1 transition-colors"
						>
							<input
								type="checkbox"
								checked={group.selectedValues.includes(option.value)}
								onChange={() => toggleSelection(option.value)}
								className="w-4 h-4 rounded border-gray-300"
							/>
							<span className="text-zinc-800 text-xs font-medium">
								{option.label}
							</span>
						</label>
					))}
					{filteredOptions.length === 0 && (
						<div className="text-xs text-gray-500 py-2">
							{searchQuery ? 'No matches' : 'No options'}
						</div>
					)}
				</div>

				{/* Divider (not after last group) */}
				{!isLast && <div className="border-t border-gray-200 my-3" />}
			</div>
		);
	};

	// Render single filter mode (backward compatible)
	const renderSingleFilter = () => {
		const searchQuery = searchQueries['single'] || '';
		const filteredOptions = (options || []).filter(
			(option) =>
				option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
				option.value.toLowerCase().includes(searchQuery.toLowerCase()),
		);

		const toggleSelection = (value: string) => {
			if (!onSelectionChange || !selectedValues) return;
			const newSelection = selectedValues.includes(value)
				? selectedValues.filter((v) => v !== value)
				: [...selectedValues, value];
			onSelectionChange(newSelection);
		};

		const selectAll = () => {
			if (!onSelectionChange || !selectedValues) return;
			const allValues = filteredOptions.map((option) => option.value);
			const newSelection = [...new Set([...selectedValues, ...allValues])];
			onSelectionChange(newSelection);
		};

		return (
			<>
				{/* Header */}
				<div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-2.5">
					<div className="flex-1 justify-start text-black text-sm font-medium leading-none">
						{title || 'Filter options'}
						{(selectedValues?.length || 0) > 0 && (
							<span className="ml-2 text-xs text-purple-600">
								({selectedValues?.length} selected)
							</span>
						)}
					</div>
					<Button
						variant="ghost"
						size="xs"
						onClick={() => setShowDropdown(false)}
						icon={X}
						className="w-4 h-4 p-0 hover:bg-gray-100"
					/>
				</div>

				{/* Content */}
				<div className="self-stretch h-96 px-4 pb-5 flex flex-col justify-start items-start gap-3">
					{/* Search */}
					{showSearch && (
						<div className="self-stretch h-9 px-2.5 rounded-md inline-flex justify-start items-center gap-2">
							<Input
								type="text"
								leftIcon={Search}
								value={searchQuery}
								onChange={(e) => setSearchQueries({ single: e.target.value })}
								placeholder={searchPlaceholder}
								className="flex-1 border-none outline-none border-gray-200"
								variant="filled"
								rightIcon={searchQuery ? X : undefined}
								onRightIconClick={
									searchQuery ? () => setSearchQueries({}) : undefined
								}
							/>
						</div>
					)}

					{/* Action Buttons */}
					<div className="self-stretch bg-white flex justify-between items-center">
						<Button
							variant="ghost"
							size="xs"
							onClick={selectAll}
							className="text-indigo-700 hover:text-indigo-800"
						>
							{searchQuery
								? `Select All (${filteredOptions.length})`
								: 'Select All'}
						</Button>
						<Button
							variant="ghost"
							size="xs"
							onClick={clearAllFilters}
							className="text-indigo-700 hover:text-indigo-800"
						>
							Clear Filter
						</Button>
					</div>

					{/* Options List */}
					<div className="self-stretch flex flex-col justify-start items-start gap-2.5 overflow-y-auto">
						{filteredOptions.map((option) => (
							<label
								key={option.id}
								className="self-stretch inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-md p-1 transition-colors"
							>
								<input
									type="checkbox"
									checked={selectedValues?.includes(option.value) || false}
									onChange={() => toggleSelection(option.value)}
									className="w-4 h-4 rounded border-gray-300"
								/>
								<div className="flex-1 inline-flex flex-col justify-start items-start">
									<div className="self-stretch justify-center text-zinc-800 text-xs font-medium">
										{option.label}
									</div>
									{option.value !== option.label && (
										<div className="self-stretch justify-center text-zinc-500 text-xs font-normal">
											{option.value}
										</div>
									)}
								</div>
							</label>
						))}
						{filteredOptions.length === 0 && (
							<div className="px-4 py-4 text-sm text-gray-500 text-center">
								{searchQuery
									? 'No options match your search'
									: 'No options found'}
							</div>
						)}
					</div>
				</div>
			</>
		);
	};

	// Render grouped filter mode
	const renderGroupedFilter = () => {
		return (
			<>
				{/* Header */}
				<div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-2.5 border-b border-gray-100">
					<div className="flex-1 justify-start text-black text-sm font-medium leading-none">
						Filters
						{getTotalSelectedCount() > 0 && (
							<span className="ml-2 text-xs text-purple-600">
								({getTotalSelectedCount()} selected)
							</span>
						)}
					</div>
					<Button
						variant="ghost"
						size="xs"
						onClick={() => setShowDropdown(false)}
						icon={X}
						className="w-4 h-4 p-0 hover:bg-gray-100"
					/>
				</div>

				{/* Groups Content */}
				<div className="self-stretch max-h-96 px-4 py-3 flex flex-col overflow-y-auto">
					{groups!.map((group, index) =>
						renderFilterGroup(group, index, index === groups!.length - 1),
					)}
				</div>

				{/* Footer Actions */}
				<div className="self-stretch px-4 py-3 border-t border-gray-100 flex justify-end">
					<Button
						variant="ghost"
						size="xs"
						onClick={clearAllFilters}
						className="text-indigo-700 hover:text-indigo-800"
					>
						Clear All Filters
					</Button>
				</div>
			</>
		);
	};

	return (
		<div className={`relative ${className}`} ref={dropdownRef}>
			<Button
				onClick={() => setShowDropdown(!showDropdown)}
				variant={variant}
				icon={Filter}
				size={size}
				className={`flex items-center gap-2 ${buttonClassName}`}
			>
				{getButtonText()}
			</Button>

			{/* Dropdown */}
			{showDropdown && (
				<div className="absolute right-0 mt-2 w-72 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-start items-center overflow-hidden shadow-lg z-20">
					{isGroupedMode ? renderGroupedFilter() : renderSingleFilter()}
				</div>
			)}
		</div>
	);
};

export default FilterDropdown;
